//@ts-nocheck
import type { GetStaticProps, GetStaticPropsContext } from 'next'
import React, { useState } from 'react'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { Layout } from '@components/common'
import {
    StoreSearch,
    StoreList,
    StoreLocatorMap
} from '@components/StoreLocater'
import cn from 'classnames'
import s from '@pagestyles/StoreLocater.module.scss'
import { useStoreList } from '@components/StoreLocater/src/hooks/useStoreList'
import { LoadingIndicator } from '@components/LoadingIndicator'
import { SEO, SEO_ENUMS } from '@components/seo'

export const STORE_CODE = 'ooka_'

export const getStaticProps: GetStaticProps = async ({
    locale
}: GetStaticPropsContext) => {
    if (!process.env.NEXT_PUBLIC_STORE_CODE?.includes(STORE_CODE)) {
        return {
            notFound: true
        }
    }
    return {
        props: {
            ...(await serverSideTranslations(locale!, ['common', 'account']))
        },
        revalidate: 14400
    }
}

const defaultCenter: google.maps.LatLngLiteral = {
    lat: 24.5,
    lng: 54.75
}

export default function StoreLocater(): JSX.Element {
    const [searchTerm, setSearchTerm] = useState('')
    const [activeStore, setActiveStore] = useState(null)
    const [expandedStoreId, setExpandedStoreId] = useState(null)
    const [filteredStores, setFilteredStores] = useState([])
    const [storeTypeFilter, setStoreTypeFilter] = useState([1, 2])
    const {
        storeList,
        loading,
        searchResultCount,
        mapApi,
        storeListMeta
    } = useStoreList(searchTerm, storeTypeFilter)
    const [
        currentPosition,
        setCurrentPosition
    ] = useState<google.maps.LatLngLiteral | null>(null)
    const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>(
        defaultCenter
    )
    const [zoom, setZoom] = useState<number>(4)
    const [isUserGeolocation, setIsUserGeolocation] = useState(false)

    const handleSearch = (term: string) => {
        setSearchTerm(term)
    }

    const handleStoreTypeFilterChange = (value: number) => {
        setStoreTypeFilter((prev) => {
            if (prev.includes(value)) {
                return prev.filter((v) => v !== value)
            } else {
                return [...prev, value]
            }
        })
    }

    if (loading) {
        return (
            <div className="mx-auto max-w-xs">
                <LoadingIndicator />
            </div>
        )
    }

    return (
        <div className={cn(s['storeLocator'])}>
            <SEO
                titleOverride={storeListMeta?.metaData?.title}
                keywordOverride={storeListMeta?.metaData?.keyword}
                descriptionOverride={storeListMeta?.metaData?.description}
                page={SEO_ENUMS.STORELOCATOR}
            />
            <StoreSearch
                storeList={storeList}
                onSearch={handleSearch}
                storeTypeFilter={storeTypeFilter}
                onStoreTypeFilterChange={handleStoreTypeFilterChange}
                searchCount={searchResultCount}
                setFilteredStores={setFilteredStores}
                setActiveStore={setActiveStore}
                setCurrentPosition={setCurrentPosition}
                currentPosition={currentPosition}
                setMapCenter={setMapCenter}
                setZoom={setZoom}
                setIsUserGeolocation={setIsUserGeolocation}
            />
            <div className={cn(s['storelocator-map'])}>
                <StoreLocatorMap
                    activeStore={activeStore}
                    setActiveStore={setActiveStore}
                    storeList={storeList}
                    expandedStoreId={expandedStoreId}
                    setExpandedStoreId={setExpandedStoreId}
                    filteredStores={filteredStores}
                    googleMapApi={mapApi}
                    center={mapCenter}
                    setCenter={setMapCenter}
                    zoom={zoom}
                    setZoom={setZoom}
                    isUserGeolocation={isUserGeolocation}
                    setIsUserGeolocation={setIsUserGeolocation}
                />
            </div>
            <StoreList
                activeStore={activeStore}
                setActiveStore={setActiveStore}
                storeList={storeList}
                expandedStoreId={expandedStoreId}
                setExpandedStoreId={setExpandedStoreId}
                filteredStores={filteredStores}
                currentPosition={currentPosition}
            />
        </div>
    )
}

StoreLocater.Layout = Layout
